import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/Header';
import { connect } from 'react-redux';
import AuthHOC from '../Auth';
import UserForm from '../../components/forms/UserForm';
import { getAll as getAllUsers, create as createUser, getById as getUserById, update as updateUser } from '../../services/userService';
import { getAll as getUserGroupLookup  } from '../../services/userGroupsService';
import ConfirmModal from '../../components/modals/common/ConfirmModal';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Stack, Button, Typography, Alert } from "@mui/material";

function UserDetailsView(props) {

  // ----- data from APIs -----
  const [availableUserGroups, setAvailableUserGroups] = useState([]);
  const [loggedInUser, setLoggedInUser] = useState([]);

  const [isDirty, setIsDirty] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // ----- loading/errors -----
  const [saving, setSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingAvailableUserGroups, setIsLoadingAvailableUserGroups] = useState(true);
  const [error, setError] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [warningMsg, setWarningMsg] = useState('');

  // ----- form state -----
  const [formData, setFormData] = useState({
    id: 0,
    username: '',
    type: '',
    name: '',
    email: '',
    x3fullname: '',
    isActive: false,
    audit: [],
    groups: [],
    groupIds: [],
    groupOptions: []
  });

  const currentUserId  = props.match?.params?.currentUserId;
  const types = ['user', 'admin', 'edm'];
  const isAdmin = props.userReducer?.userType?.toLowerCase() === 'admin';
  const loggedInUserId = props.userReducer?.userId;
  const isEditingOwnProfile = currentUserId && loggedInUserId && currentUserId === loggedInUserId.toString();
  const canAccess = isAdmin || isEditingOwnProfile;

  // Debug: Check what userType value we're getting
  console.log('UserDetailsView - userType:', props.userReducer?.userType, 'isAdmin:', isAdmin, 'isEditingOwnProfile:', isEditingOwnProfile);

  const isEditMode = !!currentUserId;

  // Redirect non-admin users who are trying to create or edit other users
  useEffect(() => {
    if (props.userReducer?.userType && !canAccess) {
      setError('Access denied. You can only edit your own profile.');
      const timer = setTimeout(() => {
        props.history.push('/site/administration/Users');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [canAccess, props.userReducer?.userType, props.history]);

  useEffect(() => {
    let cancelled = false;

    // we need logged in user details for audit.
    async function loadLoggedInUser() {
      try {
        const { user } = props.userReducer;
        if (!user) return;
        const result = await getAllUsers({ username : user }); 
        if (!result.ok) {
            setError('Error loading user details. Error status: ' + result.status + " OK: " + result.ok);
            return;
        }
        if (!cancelled) {
          setLoggedInUser(result);
        }
      } catch (error) {
        if (!cancelled) {
          setError('Could not retrieve currently logged in user.');
        }
      }
    }

    loadLoggedInUser();

    return () => {
      cancelled = true;
    };
    // by doing [props.userReducer] we're telling useEffect to update the userReducer every time it changes.
    // if you do [] it will only load once on page ready. just FYI.
  }, [props.userReducer]); 

  useEffect(() => {
    let isCancelled = false;
    let userDetails = [];
    setError('');
    setSuccessMsg('');
    setWarningMsg('');

    async function loadUserDetails() {
      try {
        setIsLoading(true);
        setError(null);

        const result = await getUserById(currentUserId);
        if (!result.ok) {
          setError('Error loading user details. Error status: ' + result.status + " OK: " + result.ok);
          return;
        }
        userDetails = result.data;

        if (isCancelled) return;
        setFormData({
          id: userDetails._id,
          username: userDetails.username || '',
          type: userDetails.type,
          name: userDetails.name || '',
          email: userDetails.email || '',
          isActive: userDetails.isActive || false,
          x3fullname: userDetails.x3fullname,
          audit: userDetails.audit ? { ...userDetails.audit } : {},
          groups: userDetails.groups || [],
          groupIds: userDetails.groupIds || []
        })
      }
      catch (err) {
        if (!isCancelled) {
          console.error('Failed to load user details', err);
          setError('Failed to load user details.')
        }
      }
      finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }
    
    async function loadAllAvailableUserGroups() {
      try {
        setIsLoadingAvailableUserGroups(true);
        setError(null);
        const result = await getUserGroupLookup({}, 'name');
        if (!result.ok) {
          setError('Error loading available user groups. Error status: ' + result.status + " OK: " + result.ok);
          return;
        }
        const formattedData = result.data.map(item => ({
          id: item._id,
          name: item.name
        }))

        if (!isCancelled){
          setAvailableUserGroups(formattedData || []);
        }
      }
      catch (err) {
        setError(err.message, 'Failed to load user groups')
      }
      finally {
        if (!isCancelled) {
          setIsLoadingAvailableUserGroups(false);
        }
      }
    }
    if (isEditMode) {
      loadUserDetails();
    }

    loadAllAvailableUserGroups();
    return () => {
      isCancelled = true;
    }
  }, [isEditMode, currentUserId]);

  // Handle success message from password change redirect
  useEffect(() => {
    const locationState = props.location?.state;
    if (locationState?.successMsg) {
      setSuccessMsg(locationState.successMsg);

      // Auto-dismiss after 5 seconds
      const timer = setTimeout(() => {
        setSuccessMsg('');
      }, 5000);

      // Clear location state to prevent message from showing again on refresh
      window.history.replaceState({}, document.title);

      return () => clearTimeout(timer);
    }
  }, [props.location?.state]);

  function getTitle() {
      const localisation = props.localisation || {};
      return localisation["menu_header_administration_user_details"]
          ? localisation["menu_header_administration_user_details"].element_lang
          : "Administration - Users";
  };

  const handleGoBackClick = () => {
    if (isDirty) {
      setShowConfirmModal(true);
      return;
    }
    else {
      props.history.push("/site/administration/Users");
    }
  }

  const handleConfirmGoBackClick = () => {
    setShowConfirmModal(false);
    props.history.push("/site/administration/Users");
  }

  const handleCancelGoBackClick = () => {
    setShowConfirmModal(false);
  }

  const handleOnChangePasswordClick = async (formData) => {
    const userId = formData.id;
    props.history.push(`/site/administration/ChangePassword/${userId}`);

  }

  const handleSubmit = async (formData) => {
    setError('');
    setSaving(true);
    setError(null);

    let userDetails = {};
    userDetails = loggedInUser.data[0];

    try {
      const userObject = {
        id: formData.id,
        username: formData.username?.trim(),
        name: formData.name?.trim(),
        email: formData.email?.trim(),
        type: formData.type,
        isActive: formData.isActive,
        x3fullname: formData.x3fullname?.trim(),
        audit: (isEditMode) ? {
          createdBy: formData.audit?.createdBy,
          createdByName: formData.audit?.createdByName,
          createdOn: formData.audit?.createdOn,
          modifiedBy: userDetails.id,
          modifiedByName: userDetails.username,
          modifiedOn: new Date()
        } : {
          createdBy: userDetails.id,
          createdByName: userDetails.username,
          createdOn: new Date(),
          modifiedBy: 0,
          modifiedByName: '',
          modifiedOn: null
        },
        groupIds: formData.groupIds
      };

      if (!isEditMode) {
        // important for this to be separate, 
        // if we don't do this we are risking re-hashing and invalidated passwords.
        userObject.password = formData.password.trim()
      }

      if (isEditMode) {
        const result = await updateUser(currentUserId, userObject);
        if (result.status === 200 || result.status === 201) {
          setSuccessMsg(`Successfully updated ${formData.username} user.`)
          setFormData(prev => ({ ...prev, ...userObject, audit: userObject.audit }));
        }
        else if (result.status === 400) {
          setError('Validation failed. No updates were made.');
        }
        else if (result.status === 404) {
          setError('Validation failed. Record not found.');
        }
        else {
          setError('Updated failed. Unexpected error occured.')
        }
      }
      else {
        const result = await createUser(userObject);

        if (result.status === 200 || result.status === 201) {
          setSuccessMsg(`Successfully created : ${formData.username} user.`);
          setFormData((prev) => ({ ...prev }));
        }
        else if (result.status === 400) {
          setError('Validation failed. User was NOT created.');
        }
        else {
          setError('Updated failed. Unexpected error occured.')
        }
      }
    } 
    catch (err) {
      setError('Failed to save user. Error: ' + err.message);
    }
    finally {
      setSaving(false);
    }
  };

  let title = getTitle();
  return (
      <div>
        <Header visible={!props.parsed.X3} />
        <div className="page-wrapper">
          <div className="s-page">
            <div className="s-page-header">
              <Stack 
                  spacing={0.5} 
                  alignItems="flex-start"
                  sx={{ mb: 3, textAlign: "left" }}
              >
                  {/* We could do breadcrumbs here in the future */}
                  <Typography variant="body2" color="text.secondary">
                      {title}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      {isEditMode ? 'Edit User Details ': 'Create New User'}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                      {isEditMode ? 'User: ' + formData.username : 'New User'}
                  </Typography>
              </Stack>
              <Button
                variant='outlined'
                type="button"
                onClick={handleGoBackClick}
                startIcon={<ArrowBackIcon />}
                sx={{
                    textTransform: 'none',
                    color: 'text.secondary',
                    borderColor: 'divider',
                    '&:hover': {
                        color: 'primary.main',
                        borderColor: 'primary.main'
                    }
                }}
                >
                  Back to Users
              </Button>
            </div>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {successMsg && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {successMsg}
              </Alert>
            )}
            {warningMsg && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {warningMsg}
              </Alert>
            )}
            <div className="s-content-panel">
              {canAccess ? (
                <UserForm
                  mode={isEditMode ? 'edit' : 'create'}
                  initialValues={isEditMode ? formData : null}
                  types={types}
                  availableUserGroups={availableUserGroups}
                  onSubmit={handleSubmit}
                  onChangePasswordClick={handleOnChangePasswordClick}
                  lockUsername={true}
                  onDirtyChange={setIsDirty}
                  isAdmin={isAdmin}
                  isEditingOwnProfile={isEditingOwnProfile}
                />
              ) : (
                <Alert severity="error" sx={{ mt: 2 }}>
                  You do not have permission to access this page. Redirecting...
                </Alert>
              )}
            </div>
            <ConfirmModal
              isOpen={showConfirmModal}
              mainTitle="Unsaved changes - Are you absolutely sure?"
              mainMessage="There are unsaved changes, are you sure you want to go back? This action cannot be undone."
              textConfirm="CONFIRM"
              textCancel="CANCEL"
              onConfirm={handleConfirmGoBackClick}
              onCancel={handleCancelGoBackClick}
              onDirtyChange={isDirty}
            >
            </ConfirmModal>
            
          </div>
        </div>
      </div>
    );
}

export default connect(function (state) { return state; })(
  AuthHOC(UserDetailsView, { name: 'administration' })
);
